import React from 'react';
import MarkdownBlogLayout from '../../components/MarkdownBlogLayout';

const BlogPatientBillingDoneRight = () => {
  const content = `
## The Conversation Nobody Wants to Have

"Mrs. Thompson, your insurance paid $340, so your remaining balance is $287."

Silence.

"I... I thought my insurance covered this."

"Well, your plan covers 80% of basic procedures, but this was classified as major, so it's only 50%. Plus you hadn't met your deductible yet."

More silence. Then: "I don't have $287 right now."

Your front desk shifts uncomfortably. The waiting room is full. Three other patients need to check out. And now you're negotiating a payment plan while everyone watches.

**Patient billing is the most uncomfortable part of running a dental practice. Here's how to make it painless.**

---

## The Patient Billing Problem

Let's be honest about what's happening in most practices:

### The Numbers

| Metric | Industry Average |
|--------|------------------|
| Patient AR over 30 days | 15-25% of total AR |
| Patient AR over 90 days | 8-12% of total AR |
| Patient balances written off annually | 3-5% of production |
| Staff time spent on patient billing | 15-25 hours/week |

For a practice producing $1.5M annually:
- **Patient AR over 90 days:** $120,000-180,000
- **Annual write-offs:** $45,000-75,000
- **Staff billing costs:** $18,000-30,000/year

**Total annual cost of patient billing problems: $183,000-285,000**

### Why It's So Hard

Patient billing is uniquely difficult because it combines:

1. **Complexity** - Insurance paid this, patient owes that, but wait there's a secondary...
2. **Emotion** - Money is stressful. Healthcare is stressful. Combine them and people get defensive.
3. **Timing** - Patients don't know what they owe until after treatment, when they're least prepared.
4. **Awkwardness** - Your team didn't sign up to be debt collectors.
5. **Volume** - Hundreds of patients, hundreds of balances, hundreds of conversations.

---

## Why Traditional Approaches Fail

### Approach A: Statements in the Mail

You send monthly statements. Patients receive them (maybe). Patients pay them (sometimes).

**Why it fails:**
- Statements get lost in junk mail
- No urgency to pay
- No easy payment options
- No explanation of charges
- Patients with questions have to call (and often don't)

**Collection rate: 40-50%**

### Approach B: Front Desk Collection

Your front desk asks for payment at checkout. They make collection calls when they have time.

**Why it fails:**
- Checkout is rushed and public
- Staff is uncomfortable asking for money
- Collection calls are time-consuming
- Patients avoid answering calls from the dentist
- Staff gets demoralized by difficult conversations

**Collection rate: 50-60%**

### Approach C: Collection Agency

You send old balances to collections. They pursue payment aggressively.

**Why it fails:**
- You only recover 10-20% of the balance
- Patients are angry and never return
- Damages your reputation
- Feels like giving up

**Collection rate: 10-20% (of what's sent)**

---

## The Better Way: Proactive, Empathetic, Automated

At Asha, we built Kianna—our AI Patient Billing Specialist—to transform how practices handle patient balances.

Kianna's approach is different:

### 1. Proactive Communication

Kianna doesn't wait for balances to age. She reaches out immediately after insurance processes:

> "Hi Sarah, this is Kianna from Smile Dental. I wanted to let you know that your insurance has processed your recent visit. Your portion is $127. I can help you take care of that now, or set up a payment plan if that works better. What would you prefer?"

**Patients appreciate being informed. They're more likely to pay when they understand what they owe and why.**

### 2. Multiple Channels

Different patients prefer different communication methods. Kianna uses all of them:

- **Text messages** - Quick, convenient, high open rates
- **Phone calls** - Personal, allows for questions
- **Email** - Detailed, provides documentation
- **Patient portal** - Self-service for those who prefer it

She learns which channel works best for each patient and prioritizes accordingly.

### 3. Clear Explanations

Confusion kills collections. Kianna explains charges clearly:

> "Your total bill was $450. Your insurance (Delta Dental) paid $323, which included your deductible of $50 that was applied. Your remaining balance is $127. This is for the filling on tooth #14 that Dr. Williams completed on January 15th."

**When patients understand their bill, they're more likely to pay it.**

### 4. Easy Payment Options

Kianna makes paying simple:

- **Pay now by phone** - Takes card over the call
- **Text-to-pay link** - One click to pay
- **Payment plans** - Automatic monthly payments
- **Financing options** - For larger balances

> "I can take a card over the phone right now, or I can text you a secure link to pay online. Which would you prefer?"

**Friction kills collections. Remove the friction.**

### 5. Empathetic Approach

Kianna is trained to handle financial conversations with empathy:

> "I understand that unexpected expenses can be stressful. We have several options to make this manageable. Would a payment plan help? We can split this into 3 monthly payments of $43 with no interest."

**Patients who feel respected pay. Patients who feel pressured avoid you.**

---

## What AI Patient Billing Looks Like

Here's a real collection sequence handled by Kianna:

### Day 1: Insurance Processes

Kianna sees that Sarah's insurance has paid. Patient balance: $127.

### Day 1: Initial Outreach (Text)

> "Hi Sarah! Your insurance has processed your recent visit to Smile Dental. Your balance is $127. Pay securely here: [link] or reply with questions. - Kianna"

### Day 3: No Response - Follow-up (Text)

> "Hi Sarah, just a friendly reminder about your $127 balance from your January visit. Need to set up a payment plan? Just reply and I can help! - Kianna"

### Day 7: No Response - Phone Call

Kianna calls Sarah:

**Kianna:** "Hi Sarah, this is Kianna from Smile Dental. I'm calling about your balance from your recent visit. Do you have a moment?"

**Sarah:** "Oh, yeah, I got the texts. I've just been busy. And honestly, I thought my insurance would cover more."

**Kianna:** "I totally understand. Insurance can be confusing. Your plan covers fillings at 80%, so they paid $323 of the $450 total. Your portion is $127. Would you like to take care of that now, or would a payment plan work better for you?"

**Sarah:** "Can I do a payment plan?"

**Kianna:** "Absolutely. I can split it into 3 payments of $43, charged automatically on the 1st of each month. Would that work?"

**Sarah:** "Yeah, that's fine."

**Kianna:** "Great, I just need a card to set that up..."

### Result

- Balance collected: $127
- Time to collection: 7 days
- Staff involvement: Zero
- Patient relationship: Preserved

---

## The Numbers Speak

Practices using Kianna see dramatic improvements:

| Metric | Traditional | With Kianna |
|--------|-------------|-------------|
| Collection rate (0-30 days) | 55% | 82% |
| Collection rate (31-60 days) | 35% | 68% |
| Collection rate (61-90 days) | 20% | 45% |
| Average days to collect | 45 | 18 |
| Staff hours on billing/week | 20 | 3 |

### ROI Calculation

For a practice with $50,000 in monthly patient AR:

**Traditional Approach:**
- Collected (55%): $27,500
- Written off: $22,500
- Staff cost: $2,000/month
- **Net collected: $25,500**

**With Kianna:**
- Collected (82%): $41,000
- Written off: $9,000
- Kianna cost: ~$800/month
- **Net collected: $40,200**

**Monthly improvement: $14,700**
**Annual improvement: $176,400**

---

## Beyond Collection: The Full Picture

Kianna doesn't just collect existing balances. She improves your entire patient financial workflow:

### Pre-Treatment Estimates

Before treatment, Kianna can contact patients with accurate estimates:

> "Hi Sarah, I wanted to let you know that for your upcoming crown, your estimated out-of-pocket will be around $450 after insurance. Would you like to discuss payment options before your appointment?"

**Patients who know what to expect are more likely to accept treatment and pay promptly.**

### Payment Plan Management

Kianna manages all payment plans:
- Sends reminders before charges
- Processes automatic payments
- Handles failed payments gracefully
- Adjusts plans when needed

### Financial Hardship Handling

For patients who truly can't pay, Kianna can:
- Offer extended payment plans
- Connect to financing options
- Flag accounts for staff review
- Handle conversations with empathy

---

## "But We Don't Want to Seem Pushy"

This is the most common concern. And it's valid—aggressive billing destroys patient relationships.

Here's the thing: **Kianna isn't aggressive. She's proactive.**

There's a difference between:
- "You owe us money. Pay now." (aggressive)
- "Your insurance processed. Here's what you owe and how to pay." (proactive)

Patients don't resent being informed. They resent being surprised, confused, or pressured.

Kianna's approach is:
- **Informative** - Explains charges clearly
- **Convenient** - Makes paying easy
- **Flexible** - Offers options
- **Respectful** - Never threatens or pressures

**Patients actually appreciate this approach.** They'd rather get a friendly text than a collections letter.

---

## The Bottom Line

Patient billing doesn't have to be painful—for you or your patients.

The choice isn't between "aggressive collection" and "writing off balances." The choice is between **losing $200,000+ per year to billing problems** and **collecting what you've earned while keeping patients happy**.

Your patients want to pay. They just need clear information, easy options, and respectful communication.

**Kianna provides all three.**

---

*Ready to transform your patient billing? [Book a demo](/book-demo) to see Kianna in action.*
`;

  return (
    <MarkdownBlogLayout
      title="Patient Billing Done Right: How to Collect More While Keeping Patients Happy"
      category="Revenue Cycle Management"
      readTime="7 minutes"
      author="Asha Team"
      date="Jan 12, 2026"
      content={content}
    />
  );
};

export default BlogPatientBillingDoneRight;
